"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import useSWR from "swr";
import { useSWRConfig } from "swr";
import {
  type NotificationInboxItem,
  type NotificationInboxPage,
  NOTIFICATIONS_UNREAD_COUNT_KEY,
} from "@/lib/notifications/cache";
import { getNotificationsInboxPageKey } from "@/lib/notifications/cache";
import { formatNotificationDateTime } from "@/lib/notifications/formatNotificationDateTime";
import {
  getPlatformChangeTypeLabel,
  isPlatformChangeType,
} from "@/lib/notifications/platform-updates";
import { recordPlatformUpdatesMetric } from "@/lib/platformUpdatesMetricsClient";
import { getValidSupabaseAccessToken } from "@/lib/supabase-access-token";
import NearMeInboxItemPresentation, {
  isNearMeDigestNotification,
  isNearMeInboxNotification,
} from "@/components/notifications/NearMeInboxItemPresentation";
import { resolveNearMeDigestClickUrl } from "@/lib/notifications/near-me-digest-copy";

type NotificationDropdownPanelProps = {
  userId?: string;
  panelId: string;
  onClose: () => void;
  limit?: number;
  /** Default: centered under mobile header. Use anchored-right for desktop header bell. */
  placement?: "floating-center" | "anchored-right";
};

type DropdownTab = "items" | "platform";

type PlatformUpdatesFeedItem = {
  id: string;
  slug: string;
  title: string;
  summary: string;
  mini_body: string;
  change_type: string;
  cta_label: string | null;
  cta_url: string | null;
  published_at: string | null;
};

type PlatformUpdatesFeedPage = {
  items: PlatformUpdatesFeedItem[];
  hasMore: boolean;
};

async function authFetch(
  path: string,
  init?: RequestInit,
): Promise<Response | null> {
  const accessToken = await getValidSupabaseAccessToken();
  if (!accessToken) return null;
  return fetch(path, {
    ...init,
    headers: {
      ...init?.headers,
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    },
  });
}

export default function NotificationDropdownPanel({
  userId,
  panelId,
  onClose,
  limit = 6,
  placement = "floating-center",
}: NotificationDropdownPanelProps) {
  const router = useRouter();
  const { mutate: mutateGlobal } = useSWRConfig();
  const [isVisible, setIsVisible] = useState(false);
  const [activeTab, setActiveTab] = useState<DropdownTab>("items");

  const { data: unreadCountData, mutate: mutateUnreadCount } = useSWR<{
    unreadCount: number;
  }>(userId ? `notifications-dropdown-unread:${userId}` : null, async () => {
    const res = await authFetch("/api/notifications/inbox?count_only=true");
    if (!res?.ok) {
      throw new Error("Failed to load unread count");
    }
    return (await res.json()) as { unreadCount: number };
  });

  const {
    data: itemData,
    error: itemError,
    isLoading: itemLoading,
    mutate: mutateItems,
  } = useSWR<NotificationInboxPage>(
    userId ? `notifications-dropdown:${userId}:${limit}:item` : null,
    async () => {
      const key = getNotificationsInboxPageKey(0, limit, "item");
      const [, domain, offset, pageLimit] = key.split(":");
      const query = new URLSearchParams({
        limit: pageLimit,
        offset,
        notification_domain: domain,
      });
      const res = await authFetch(
        `/api/notifications/inbox?${query.toString()}`,
      );
      if (!res?.ok) {
        throw new Error("Failed to load item notifications");
      }
      return (await res.json()) as NotificationInboxPage;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 20_000,
    },
  );

  const {
    data: platformFeedData,
    error: platformError,
    isLoading: platformLoading,
  } = useSWR<PlatformUpdatesFeedPage>(
    userId ? `notifications-dropdown:${userId}:${limit}:platform` : null,
    async () => {
      const query = new URLSearchParams({
        limit: String(limit),
        offset: "0",
      });
      const res = await fetch(`/api/platform-updates?${query.toString()}`);
      if (!res.ok) {
        throw new Error("Failed to load platform updates");
      }
      return (await res.json()) as PlatformUpdatesFeedPage;
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: true,
      dedupingInterval: 20_000,
    },
  );

  const { data: platformUnreadCountData } = useSWR<{ unreadCount: number }>(
    userId ? `notifications-dropdown-unread-platform:${userId}` : null,
    async () => {
      const res = await authFetch(
        "/api/notifications/inbox?count_only=true&notification_domain=platform",
      );
      if (!res?.ok) {
        throw new Error("Failed to load platform unread count");
      }
      return (await res.json()) as { unreadCount: number };
    },
  );

  // Realtime refresh for user_notifications is centralized in NotificationUserRealtimeSubscriber.

  const items = itemData?.items ?? [];
  const platformItems = platformFeedData?.items ?? [];
  const itemUnreadCount = itemData?.unreadCount ?? 0;
  const platformUnreadCount = platformUnreadCountData?.unreadCount ?? 0;
  const isLoading = activeTab === "items" ? itemLoading : platformLoading;
  const error = activeTab === "items" ? itemError : platformError;

  const formatUnreadBadge = (count: number) => {
    if (count <= 0) return null;
    return count > 99 ? "99+" : String(count);
  };

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      setIsVisible(true);
    });
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    if (typeof unreadCountData?.unreadCount === "number") {
      void mutateGlobal(
        NOTIFICATIONS_UNREAD_COUNT_KEY,
        unreadCountData.unreadCount,
        {
          revalidate: false,
        },
      );
    }
  }, [unreadCountData?.unreadCount, mutateGlobal]);

  useEffect(() => {
    if (activeTab !== "platform") return;
    void recordPlatformUpdatesMetric("platform_updates_dropdown_tab_open", {
      surface: "notification_bell_dropdown",
    });
  }, [activeTab]);

  const setTab = (nextTab: DropdownTab) => {
    setActiveTab(nextTab);
  };

  const markItemReadAndNavigate = async (item: NotificationInboxItem) => {
    if (!item.read_at) {
      const res = await authFetch(`/api/notifications/inbox/${item.id}`, {
        method: "PATCH",
      });
      if (res?.ok) {
        const nextData = await mutateItems(
          (current) => {
            if (!current) return current;
            const now = new Date().toISOString();
            const nextItems = current.items.map((p) =>
              p.id === item.id ? { ...p, read_at: now } : p,
            );
            const nextUnreadCount = Math.max(0, (current.unreadCount ?? 0) - 1);
            const currentUnread = unreadCountData?.unreadCount ?? 0;
            void mutateUnreadCount(
              { unreadCount: Math.max(0, currentUnread - 1) },
              { revalidate: false },
            );
            return {
              ...current,
              items: nextItems,
              unreadCount: nextUnreadCount,
            };
          },
          { revalidate: false },
        );
        const nextUnread = unreadCountData?.unreadCount;
        if (typeof nextUnread === "number") {
          await mutateGlobal(
            NOTIFICATIONS_UNREAD_COUNT_KEY,
            Math.max(0, nextUnread - 1),
            {
              revalidate: false,
            },
          );
        } else if (nextData) {
          void mutateUnreadCount();
        }
      }
    }

    onClose();
    const destination = isNearMeDigestNotification(item.event_type)
      ? resolveNearMeDigestClickUrl(item.metadata, item.url)
      : item.url || "/notifications?tab=items";
    router.push(destination);
  };

  const openPlatformUpdatesTab = (item: PlatformUpdatesFeedItem) => {
    void recordPlatformUpdatesMetric("platform_updates_detail_open", {
      surface: "notification_bell_dropdown",
      notification_id: item.id,
    });
    onClose();
    router.push("/notifications?tab=platform");
  };

  const viewAllHref =
    activeTab === "platform"
      ? "/notifications?tab=platform"
      : "/notifications?tab=items";

  const positionClass =
    placement === "anchored-right"
      ? "absolute right-0 top-full z-[10030] mt-2 w-[min(22rem,calc(100vw-2rem))] min-w-[16rem]"
      : "fixed left-1/2 top-[5.25rem] w-[min(66.666vw,calc(100vw-2rem))] min-w-[16rem] -translate-x-1/2 z-[10030]";

  return (
    <div
      id={panelId}
      role="dialog"
      aria-label="Recent notifications"
      className={`${positionClass} overflow-hidden rounded-xl border border-border bg-surface shadow-xl transition-all duration-200 ease-out ${
        isVisible ? "translate-y-0 opacity-100" : "-translate-y-1 opacity-0"
      }`}
    >
      <div className="border-b border-border px-4 py-3">
        <h3 className="text-sm font-semibold text-foreground">
          Recent notifications
        </h3>
        <div className="mt-2 inline-flex rounded-lg border border-border bg-surface p-1 text-xs">
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-colors ${
              activeTab === "items"
                ? "bg-foreground text-background"
                : "text-text-light hover:bg-tertiary"
            }`}
            onClick={() => setTab("items")}
          >
            Items
            {itemUnreadCount > 0 ? (
              <span
                className={`inline-flex min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] leading-4 ${
                  activeTab === "items"
                    ? "bg-surface/20 text-white"
                    : "bg-primary-100 text-primary-700"
                }`}
              >
                {formatUnreadBadge(itemUnreadCount)}
              </span>
            ) : null}
          </button>
          <button
            type="button"
            className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 font-medium transition-colors ${
              activeTab === "platform"
                ? "bg-foreground text-background"
                : "text-text-light hover:bg-tertiary"
            }`}
            onClick={() => setTab("platform")}
          >
            Platform Updates
            {platformUnreadCount > 0 ? (
              <span
                className={`inline-flex min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] leading-4 ${
                  activeTab === "platform"
                    ? "bg-surface/20 text-white"
                    : "bg-primary-100 text-primary-700"
                }`}
              >
                {formatUnreadBadge(platformUnreadCount)}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      <div className="max-h-80 overflow-y-auto">
        {isLoading ? (
          <div className="px-4 py-4 text-sm text-text-light">
            Loading notifications...
          </div>
        ) : error ? (
          <div className="px-4 py-4 text-sm text-red-600">
            Could not load notifications right now.
          </div>
        ) : activeTab === "items" && items.length === 0 ? (
          <div className="px-4 py-4 text-sm text-text-light">
            You have no item notifications yet.
          </div>
        ) : activeTab === "platform" && platformItems.length === 0 ? (
          <div className="px-4 py-4 text-sm text-text-light">
            No platform updates yet.
          </div>
        ) : activeTab === "items" ? (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`w-full px-4 py-3 text-left hover:bg-secondary ${
                    !item.read_at ? "bg-primary-50/40" : ""
                  }`}
                  onClick={() => void markItemReadAndNavigate(item)}
                >
                  {isNearMeInboxNotification(item.event_type) ? (
                    <NearMeInboxItemPresentation
                      eventType={item.event_type}
                      title={item.title}
                      body={item.body}
                      miniBody={item.mini_body}
                      metadata={item.metadata}
                      createdAt={item.created_at}
                      createdAtLabel={formatNotificationDateTime(
                        item.created_at,
                      )}
                      variant="compact"
                    />
                  ) : (
                    <>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm font-medium text-foreground line-clamp-1">
                          {item.title}
                        </span>
                        <time
                          className="text-[11px] text-text-light shrink-0 text-right leading-tight"
                          dateTime={item.created_at}
                        >
                          {formatNotificationDateTime(item.created_at)}
                        </time>
                      </div>
                      <p className="mt-1 text-xs text-text-light line-clamp-2">
                        {item.body}
                      </p>
                    </>
                  )}
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <ul className="divide-y divide-border">
            {platformItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className="w-full px-4 py-3 text-left hover:bg-secondary"
                  onClick={() => openPlatformUpdatesTab(item)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm font-medium text-foreground line-clamp-1">
                      {item.title}
                    </span>
                    <time
                      className="text-[11px] text-text-light shrink-0 text-right leading-tight"
                      dateTime={item.published_at ?? undefined}
                    >
                      {item.published_at
                        ? formatNotificationDateTime(item.published_at)
                        : "--"}
                    </time>
                  </div>
                  <p className="mt-1 text-xs text-text-light line-clamp-2">
                    {item.mini_body ?? item.summary}
                  </p>
                  {isPlatformChangeType(item.change_type) ? (
                    <span className="mt-1 inline-flex rounded-full bg-secondary px-2 py-0.5 text-[10px] font-medium text-foreground">
                      {getPlatformChangeTypeLabel(item.change_type)}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-border p-2">
        <Link
          href={viewAllHref}
          className="block rounded-md px-3 py-2 text-center text-sm font-medium text-primary hover:bg-primary-50"
          onClick={onClose}
        >
          View all notifications
        </Link>
      </div>
    </div>
  );
}
